import React, { useState, useEffect, useRef } from "react";
import { Survey } from "survey-react-ui";
import { Model, slk, surveyLocalization } from "survey-core";
import "survey-core/i18n/index";
import SurveyService from "../api/rest/Survey.service";
import { QuestionnaireResponse, SurveyResponse } from "../api/interfaces";
import { createCompassSurveyTheme } from "../theme/surveyTheme";
import { NoSurveyDataPage } from "./index";
import Loading from "./Loading";
import SubmittedResponseThankyou from "./SubmittedResponseThankyou";
import SurveyClosedPage from "./SurveyClosedPage";
import SurveyErrorPage from "./SurveyErrorPage";
import InvalidUrlPage from "./InvalidUrlPage";
import PinLandingPage from "./PinLandingPage";
import {
  getSurveyUrlParams,
  validateSurveyUrlParams,
} from "../utils/urlParams";
import { useSnackbar } from "../hooks/useSnackbar";
import {
  saveSurveyToLocalStorage,
  loadSurveyFromLocalStorage,
  clearSurveyFromLocalStorage,
  clearExpiredSurveys,
} from "../utils/surveyStorage";
import { syncAllCheckboxSelectionDescriptions } from "./checkboxSelectionInstruction";
import { registerHtmlQuestion } from "./customQuestions/HtmlQuestion";
import LanguageSelector from "./LanguageSelector";
import { getTranslation } from "../locales";
import "../styles/matrixStyles.css";
import "../styles/htmlQuestionStyles.css";

// Register custom HTML question type
registerHtmlQuestion();

// Load all SurveyJS locales so surveys can be taken in any supported language
surveyLocalization.supportedLocales = [];
surveyLocalization.defaultLocale = "en";

// Register locales not natively supported by SurveyJS.
// Must set both locales (for string lookups) and localeNames (for display in dropdowns).
const unsupportedLocaleNames: Record<string, string> = { km: "ខ្មែរ" };
Object.entries(unsupportedLocaleNames).forEach(([code, name]) => {
  if (!surveyLocalization.locales[code]) {
    surveyLocalization.locales[code] = {};
  }
  (surveyLocalization as any).localeNames[code] = name;
});

// Activate SurveyJS license
if (process.env.REACT_APP_SURVEYJS_LICENSE_KEY) {
  slk(process.env.REACT_APP_SURVEYJS_LICENSE_KEY);
}

const resolveTranslatable = (value: any, locale: string): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value[locale] ?? value["default"] ?? null;
};

const SurveyRenderer: React.FC = () => {
  const [questionnaireResponse, setQuestionnaireResponse] =
    useState<QuestionnaireResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [survey, setSurvey] = useState<Model | null>(null);
  const [error, setError] = useState(false);
  const [is404Error, setIs404Error] = useState(false);
  const [locale, setLocale] = useState<string>("en");
  const [availableLocales, setAvailableLocales] = useState<string[]>([]);
  const [isPinVerified, setIsPinVerified] = useState(false);
  const { setMessage } = useSnackbar();

  // Refs for partial save timer (refs avoid stale closures in interval/beforeunload callbacks)
  const isDirtyRef = useRef(false);
  const isCompletedRef = useRef(false);
  const surveyDataRef = useRef<{ data: any; currentPage: number } | null>(null);
  const surveyServiceRef = useRef<SurveyService | null>(null);
  const urlParamsRef = useRef<{
    surveyGuid: string;
    respondentGuid?: string;
  } | null>(null);

  const mapApiLocaleToSurveyJsLocale = (apiLocale: string): string => {
    if (apiLocale === "default") return "en";
    return apiLocale;
  };

  const detectBrowserLanguage = (): string => {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    // Try full BCP 47 tag first (e.g. "zh-CN" -> "zh-cn", "pt-BR" -> "pt-br")
    const fullTag = browserLang.toLowerCase();
    if (surveyLocalization.locales[fullTag]) return fullTag;
    // Fall back to primary language code (e.g. "en" from "en-US")
    const primaryLang = fullTag.split("-")[0];
    if (surveyLocalization.locales[primaryLang]) return primaryLang;
    return "en";
  };

  // Determine the best locale to use based on priority:
  // 1. Browser language (if supported by the survey)
  // 2. First available locale from survey
  // 3. Default to English
  const determineBestLocale = (availableLocales: string[]): string => {
    // Check if there are available locales
    if (!availableLocales || availableLocales.length === 0) {
      return "en";
    }

    // Map available locales to SurveyJS format
    const mappedAvailableLocales = availableLocales.map(
      mapApiLocaleToSurveyJsLocale,
    );

    // Priority 1: Check browser language
    const browserLocale = detectBrowserLanguage();
    if (mappedAvailableLocales.includes(browserLocale)) {
      return browserLocale;
    }

    // Priority 2: Use first available locale
    const firstAvailableLocale = mapApiLocaleToSurveyJsLocale(
      availableLocales[0],
    );

    // Priority 3: Default to English if nothing else matches
    return mappedAvailableLocales.includes("en") ? "en" : firstAvailableLocale;
  };

  // Handle locale change
  const handleLocaleChange = (newLocale: string) => {
    setLocale(newLocale);
    if (survey) {
      survey.locale = newLocale;
    }
  };

  // Handle PIN submission
  const handlePinSubmit = async (pin: string): Promise<boolean> => {
    try {
      const urlParams = getSurveyUrlParams();
      if (!urlParams) {
        return false;
      }

      const { surveyGuid } = urlParams;
      const surveyService = new SurveyService();

      const result = await surveyService.verifyPIN({
        pin: pin,
        surveyGuid: surveyGuid,
      });

      if (result.success) {
        // Redirect based on isTest flag
        // Check environment: dev uses root (/), UAT/prod uses /index.html
        const isDev = window.location.hostname === "localhost";
        const basePath = isDev ? "/" : "/index.html";
        const baseUrl = `${window.location.origin}${basePath}`;
        let surveyUrl: string;

        if (result.isTest) {
          // For test surveys, only include surveyGuid and isTest flag
          surveyUrl = `${baseUrl}?surveyGuid=${surveyGuid}&isTest=true`;
        } else {
          // For not test surveys, include respondentGuid
          surveyUrl = `${baseUrl}?surveyGuid=${surveyGuid}&respondentGuid=${result.respondentLinkGuid}`;
        }

        window.location.href = surveyUrl;
        return true;
      }

      // Show error message from API if available
      if (result.errorMessage) {
        console.warn("PIN verification failed:", result.errorMessage);
      }

      return false;
    } catch (error) {
      console.error("PIN verification error:", error);
      return false;
    }
  };

  useEffect(() => {
    let cleanupResize: (() => void) | null = null;
    let partialSaveInterval: ReturnType<typeof setInterval> | null = null;
    let handleBeforeUnload: ((e: BeforeUnloadEvent) => void) | null = null;

    const loadSurveyData = async () => {
      setLoading(true);
      try {
        // Clear expired surveys on load
        clearExpiredSurveys();

        // Validate URL parameters gracefully
        const validation = validateSurveyUrlParams();
        if (!validation.isValid) {
          setMessage(validation.error || "Invalid URL parameters", true);
          setLoading(false);
          return;
        }

        const urlParams = getSurveyUrlParams();
        if (!urlParams) {
          setMessage("Failed to parse URL parameters", true);
          setLoading(false);
          return;
        }

        const { surveyGuid, respondentGuid, isTest } = urlParams;
        // respondentGuid will be used for respondent-specific functionality

        // Initialize survey service
        const surveyService = new SurveyService();

        // Fetch survey data using SurveyService
        const response = await surveyService.getQuestionnaire(
          surveyGuid,
          respondentGuid,
        );
        setQuestionnaireResponse(response);

        // Extract available locales from the survey structure
        const localesFromApi = response.structure?.locales;
        if (
          localesFromApi &&
          Array.isArray(localesFromApi) &&
          localesFromApi.length > 0
        ) {
          setAvailableLocales(localesFromApi);

          // Determine the best locale based on saved preference, browser language, or defaults
          const bestLocale = determineBestLocale(localesFromApi);
          setLocale(bestLocale);
        } else {
          // No locales available, set to empty and default to English
          setAvailableLocales([]);
          setLocale("en");
        }

        // If survey is closed, don't create survey model
        if (!response.isOpen) {
          setLoading(false);
          return;
        }

        // If already submitted, don't create survey model
        if (response.isSubmitted) {
          setLoading(false);
          return;
        }

        // If PIN is required, check if already verified
        // respondentGuid in URL means PIN was verified for all kinds of surveys
        // For test surveys: isTest=true in URL means PIN was verified
        // If neither exists, show PIN landing page
        if (response.isPIN && !respondentGuid && !isTest) {
          setLoading(false);
          return;
        }

        // If PIN survey and verified (either via respondentGuid or isTest), mark as verified
        if (response.isPIN && (respondentGuid || isTest)) {
          setIsPinVerified(true);
        }

        // Store navigateToUrl and remove it to prevent auto-redirect
        const navigateToUrl = response.structure.navigateToUrl;
        const surveyStructure = { ...response.structure };
        if (navigateToUrl) {
          delete surveyStructure.navigateToUrl;
        }

        // Create survey model
        const surveyModel = new Model(surveyStructure);

        // Default ("onComplete") only clears a hidden question's value at survey
        // completion, and doesn't cascade to re-evaluate visibleIf conditions that
        // depend on that value. "onHidden" clears immediately when a question is
        // hidden, which re-triggers dependent visibleIf evaluation and hides chained
        // questions (Q3 depends on Q2 depends on Q1) as soon as the chain breaks.
        surveyModel.clearInvisibleValues = "onHidden";

        // Inject respondent demographic values as SurveyJS variables so visibleIf
        // conditions can reference them (e.g. {Department} = 'Engineering')
        if (response.demographics) {
          Object.entries(response.demographics).forEach(([key, value]) => {
            surveyModel.setVariable(key, value);
          });
          // After setting variables, SurveyJS re-evaluates page visibility. If the
          // first page has a visibleIf condition, it was evaluated during model creation
          // before demographics were available, so it was hidden and the model's internal
          // current-page pointer was set to the next visible page. Reset to 0 so the
          // first now-visible page is shown. The saved-data block below will override
          // this for users resuming a partially completed survey.
          surveyModel.currentPageNo = 0;
        }

        // SurveyJS evaluates page `visibleIf` during Model construction, before
        // we set the demographic variables above (notably `360Relationship` on
        // 360 surveys). Pages gated on those variables are invisible at that
        // moment, so the model can land on whichever always-visible page comes
        // last. setVariable updates visibility but doesn't move the cursor, so
        // we re-anchor to page 0 here. Any saved page from a returning visit
        // is applied further down and will override this.
        surveyModel.currentPageNo = 0;

        syncAllCheckboxSelectionDescriptions(surveyModel);

        // Render HTML content in page/question descriptions
        surveyModel.onTextMarkdown.add((_, options) => {
          if (options.name === "description") {
            options.html = options.text;
          }
        });

        // For matrix questions, ensure eachRowRequired is set when isRequired is true
        // This ensures all rows must be answered when the question is marked as required
        surveyModel.getAllQuestions().forEach((question) => {
          if (question.getType() === "matrix") {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const matrixQuestion = question as any;
            if (question.isRequired) {
              matrixQuestion.eachRowRequired = true;
            }
          }
        });

        // Handle responsive matrix display mode for tablets (768-1024px)
        // On smaller screens like iPad mini, switch matrix questions to list mode for better layout
        const updateMatrixDisplayMode = () => {
          const width = window.innerWidth;
          surveyModel.getAllQuestions().forEach((question) => {
            if (
              question.getType() === "matrix" ||
              question.getType() === "matrixdropdown" ||
              question.getType() === "matrixdynamic"
            ) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const matrixQuestion = question as any;
              // For tablets (768-1024px) and phones (<768px), use list mode for better display
              // For larger screens (>1024px), use table mode
              if (width <= 1024) {
                matrixQuestion.displayMode = "list";
                // In list mode, remove fixed row title width to allow natural full-width layout
                matrixQuestion.rowTitleWidth = "";
              } else {
                matrixQuestion.displayMode = "table";
                // In table mode, set row title width for better column layout
                // Only set if not already specified in survey JSON
                if (!matrixQuestion.rowTitleWidth) {
                  matrixQuestion.rowTitleWidth = "400px";
                }
                // Set column min width for compact table layout
                if (!matrixQuestion.columnMinWidth) {
                  matrixQuestion.columnMinWidth = "50px";
                }
              }
            }
          });
        };

        // Set initial display mode
        updateMatrixDisplayMode();

        // Update display mode on window resize
        const handleResize = () => {
          updateMatrixDisplayMode();
        };

        window.addEventListener("resize", handleResize);

        // Store cleanup function
        cleanupResize = () => {
          window.removeEventListener("resize", handleResize);
        };

        surveyModel.showProgressBar = true;
        surveyModel.showProgressBar = "bottom";
        surveyModel.progressBarType = "questions";

        // Load saved data: prefer server-side partial response over localStorage
        const savedServerData = response.partialResponse
          ? JSON.parse(response.partialResponse)
          : null;
        const savedLocalData = loadSurveyFromLocalStorage(
          surveyGuid,
          respondentGuid,
          isTest,
        );
        const savedSurveyData = savedServerData || savedLocalData;
        if (savedSurveyData) {
          surveyModel.data = savedSurveyData.data;
          if (
            savedSurveyData.currentPage >= 0 &&
            savedSurveyData.currentPage < surveyModel.pages.length
          ) {
            surveyModel.currentPageNo = savedSurveyData.currentPage;
          }
        }

        // Apply custom Compass theme, overriding with any saved theme values
        // If data was loaded from localStorage but not from server, mark dirty
        // so the partial-save timer syncs it to the server on the next tick
        if (!savedServerData && savedLocalData && respondentGuid && !isTest) {
          surveyDataRef.current = {
            data: savedLocalData.data,
            currentPage: savedLocalData.currentPage,
          };
          isDirtyRef.current = true;
        }
        const compassTheme = createCompassSurveyTheme();
        if (surveyStructure.themeAccentColor) {
          compassTheme.cssVariables["--sjs-primary-backcolor"] =
            surveyStructure.themeAccentColor;
          compassTheme.cssVariables["--sjs-checkbox-backcolor"] =
            surveyStructure.themeAccentColor;
          compassTheme.cssVariables["--sjs-radio-backcolor"] =
            surveyStructure.themeAccentColor;
        }
        if (surveyStructure.themeFontFamily) {
          compassTheme.cssVariables["--sjs-font-family"] =
            surveyStructure.themeFontFamily;
        }
        if (surveyStructure.themeFontSize) {
          compassTheme.cssVariables["--sjs-font-size"] =
            surveyStructure.themeFontSize;
        }
        surveyModel.applyTheme(compassTheme);

        // Set the survey locale
        surveyModel.locale = locale;

        // Store refs for interval/beforeunload access
        surveyServiceRef.current = surveyService;
        urlParamsRef.current = { surveyGuid, respondentGuid };

        // Set up survey event handlers
        surveyModel.onValueChanged.add((sender) => {
          saveSurveyToLocalStorage(
            surveyGuid,
            respondentGuid,
            isTest,
            sender.data,
            sender.currentPageNo,
          );
          surveyDataRef.current = {
            data: sender.data,
            currentPage: sender.currentPageNo,
          };
          isDirtyRef.current = true;
        });

        surveyModel.onCurrentPageChanged.add((sender) => {
          saveSurveyToLocalStorage(
            surveyGuid,
            respondentGuid,
            isTest,
            sender.data,
            sender.currentPageNo,
          );
          surveyDataRef.current = {
            data: sender.data,
            currentPage: sender.currentPageNo,
          };
          isDirtyRef.current = true;
        });

        surveyModel.onComplete.add(async (sender) => {
          // Stop partial saves — the response is being finalized
          isDirtyRef.current = false;
          isCompletedRef.current = true;

          try {
            // Skip API call if this is a test survey
            if (!isTest) {
              const responseData: SurveyResponse = {
                SurveyGuid: surveyGuid,
                Response: JSON.stringify(sender.data),
              };

              if (respondentGuid) {
                responseData.RespondentGuid = respondentGuid;
              }

              await surveyService.submitResponse(responseData);
            }

            // Clear localStorage on successful submission
            clearSurveyFromLocalStorage(surveyGuid, respondentGuid, isTest);

            // Redirect after delay if navigateToUrl exists
            if (navigateToUrl) {
              setTimeout(() => {
                window.location.href = navigateToUrl;
              }, 1000);
            }
          } catch (error) {
            const t = getTranslation(locale);
            setMessage(t.submitFailed, true);
          }
        });

        // Push partial response to server if data has changed
        const pushPartialResponse = () => {
          if (
            !isDirtyRef.current ||
            !surveyDataRef.current ||
            !urlParamsRef.current?.respondentGuid
          )
            return;
          if (!surveyServiceRef.current) return;

          isDirtyRef.current = false;
          const payload: SurveyResponse = {
            SurveyGuid: urlParamsRef.current.surveyGuid,
            RespondentGuid: urlParamsRef.current.respondentGuid,
            Response: JSON.stringify(surveyDataRef.current),
            IsComplete: false,
            ClientTimestamp: new Date().toISOString(),
          };

          surveyServiceRef.current.submitResponse(payload).catch(() => {
            // If save failed, mark dirty again so next interval retries
            isDirtyRef.current = true;
          });
        };

        // Set up 5-second partial save timer (only for respondent-link surveys)
        if (respondentGuid && !isTest) {
          partialSaveInterval = setInterval(pushPartialResponse, 10000);
        }

        // Warn user about data loss and push unsaved partial response when closing
        handleBeforeUnload = (e: BeforeUnloadEvent) => {
          if (
            !isDirtyRef.current ||
            !surveyDataRef.current ||
            isCompletedRef.current
          )
            return;

          // Push partial response to server immediately
          if (
            urlParamsRef.current?.respondentGuid &&
            surveyServiceRef.current
          ) {
            const payload: SurveyResponse = {
              SurveyGuid: urlParamsRef.current.surveyGuid,
              RespondentGuid: urlParamsRef.current.respondentGuid,
              Response: JSON.stringify(surveyDataRef.current),
              IsComplete: false,
              ClientTimestamp: new Date().toISOString(),
            };
            const url = surveyServiceRef.current.makeUrl("/Response");
            // text/plain avoids CORS preflight (OPTIONS), which doesn't complete
            // reliably during page unload — see Config.service.ts for details
            const blob = new Blob([JSON.stringify(payload)], {
              type: "text/plain",
            });
            navigator.sendBeacon(url, blob);
          }

          isDirtyRef.current = false;
          e.preventDefault();
          e.returnValue = "";
        };

        if (respondentGuid && !isTest) {
          window.addEventListener("beforeunload", handleBeforeUnload);
        }

        setSurvey(surveyModel);
        setLoading(false);
      } catch (err) {
        // Check if it's a 404 error (invalid respondentGuid)
        if ((err as any)?.status === 404) {
          setIs404Error(true);
        } else {
          setError(true);
        }
        setLoading(false);
      }
    };

    loadSurveyData();

    // Return cleanup function from useEffect
    return () => {
      if (cleanupResize) cleanupResize();
      if (partialSaveInterval) clearInterval(partialSaveInterval);
      if (handleBeforeUnload)
        window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <>
        <LanguageSelector
          currentLocale={locale}
          availableLocales={availableLocales}
          onLocaleChange={handleLocaleChange}
        />
        <Loading locale={locale} />
      </>
    );
  }

  // If 404 error occurred (invalid respondentGuid), show invalid URL page
  if (is404Error) {
    return (
      <>
        <LanguageSelector
          currentLocale={locale}
          availableLocales={availableLocales}
          onLocaleChange={handleLocaleChange}
        />
        <InvalidUrlPage locale={locale} />
      </>
    );
  }

  // If error occurred, show error page
  if (error) {
    return (
      <>
        <LanguageSelector
          currentLocale={locale}
          availableLocales={availableLocales}
          onLocaleChange={handleLocaleChange}
        />
        <SurveyErrorPage locale={locale} />
      </>
    );
  }

  // If survey is closed, show closed page
  if (questionnaireResponse && !questionnaireResponse.isOpen) {
    return (
      <>
        <LanguageSelector
          currentLocale={locale}
          availableLocales={availableLocales}
          onLocaleChange={handleLocaleChange}
        />
        <SurveyClosedPage
          locale={locale}
          customTitle={resolveTranslatable(
            questionnaireResponse?.structure?.surveyClosedTitle,
            locale,
          )}
          customSubtitle={resolveTranslatable(
            questionnaireResponse?.structure?.surveyClosedSubtitle,
            locale,
          )}
          customMessage={resolveTranslatable(
            questionnaireResponse?.structure?.surveyClosedMessage,
            locale,
          )}
        />
      </>
    );
  }

  // If already submitted (on load), show thank you page
  if (questionnaireResponse?.isSubmitted) {
    return (
      <>
        <LanguageSelector
          currentLocale={locale}
          availableLocales={availableLocales}
          onLocaleChange={handleLocaleChange}
        />
        <SubmittedResponseThankyou
          locale={locale}
          customTitle={resolveTranslatable(
            questionnaireResponse?.structure?.submittedThankYouTitle,
            locale,
          )}
          customSubtitle={resolveTranslatable(
            questionnaireResponse?.structure?.submittedThankYouSubtitle,
            locale,
          )}
          customMessage={resolveTranslatable(
            questionnaireResponse?.structure?.submittedThankYouMessage,
            locale,
          )}
        />
      </>
    );
  }

  // If PIN is required and not verified, show PIN landing page
  if (questionnaireResponse?.isPIN && !isPinVerified) {
    const surveyTitle =
      resolveTranslatable(questionnaireResponse.structure?.title, locale) ||
      "Survey";

    return (
      <>
        <LanguageSelector
          currentLocale={locale}
          availableLocales={availableLocales}
          onLocaleChange={handleLocaleChange}
        />
        <PinLandingPage
          locale={locale}
          surveyTitle={surveyTitle}
          onPinSubmit={handlePinSubmit}
        />
      </>
    );
  }

  if (
    !survey ||
    !questionnaireResponse?.structure ||
    !questionnaireResponse.structure.pages
  ) {
    return (
      <>
        <LanguageSelector
          currentLocale={locale}
          availableLocales={availableLocales}
          onLocaleChange={handleLocaleChange}
        />
        <NoSurveyDataPage locale={locale} />
      </>
    );
  }

  return (
    <>
      <LanguageSelector
        currentLocale={locale}
        availableLocales={availableLocales}
        onLocaleChange={handleLocaleChange}
      />
      <Survey model={survey} />
    </>
  );
};

export default SurveyRenderer;
